import type React from "react";
import { Cell, Label, Pie, PieChart } from "recharts";

import { useTheme } from "styled-components";
import type { ElearningThemeInterface } from "themes/elearningInterface.theme";

import { Heading3 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssButton, CssFlex, CssIcon } from "styles/helpers/layout";

import type { Variant as ButtonVariant } from "components/Button/Button.model";

import { SanitizedHtml } from "components/SanitizedHtml";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StyledIconContainer, StyledModuleCard } from "./BaseModuleCard.styled";

interface BaseModuleCardProps {
  dataCy?: string;
  moduleId: number | string;
  moduleTitle: string;
  moduleDescription: string;
  isPlaceholderModule?: boolean;
  avgProgress?: number;
  actionButtonLabel: string;
  actionButtonVariant?: ButtonVariant;
  onCardClick?: () => void;
  onActionButtonClick: () => void;
  extraCardContent?: React.ReactNode;
}

export const BaseModuleCard: React.FC<BaseModuleCardProps> = ({
  dataCy = "module-card",
  isPlaceholderModule = false,
  onCardClick,
  moduleId,
  moduleTitle,
  moduleDescription,
  avgProgress = 0,
  actionButtonVariant = "primary",
  actionButtonLabel,
  onActionButtonClick,
  extraCardContent,
}) => {
  const { t } = useTranslation("homepage");
  const theme = useTheme() as ElearningThemeInterface;

  // Variables for progress chart
  const isInProgress = avgProgress < 100;
  const progressChartData = useMemo(
    () => [
      {
        id: `module-${moduleId}-completed-progress-bar`,
        dataCy: "completion-progress-bar",
        value: avgProgress,
        color: isInProgress ? theme.palette.primary.brand : theme.palette.feedback.success.brand,
      },
      {
        id: `module-${moduleId}-pending-progress-bar`,
        value: 100 - avgProgress,
        color: theme.palette.neutral._900,
      },
    ],
    [avgProgress, theme],
  );

  return (
    <StyledModuleCard data-cy={dataCy} data-module-id={moduleId} onClick={onCardClick}>
      <CssFlex flexDirection="column" rowGap={1.5} alignItems="center" padding="1rem 0">
        {isPlaceholderModule && (
          <StyledIconContainer alignItems="center" justifyContent="center">
            <CssIcon iconName="far fa-hourglass" iconSize="lg" />
          </StyledIconContainer>
        )}
        {/* -- Chart with overall progress -- */}
        {!isPlaceholderModule && (
          <PieChart height={190} width={190} style={{ margin: "-23px 0" }}>
            <Pie
              aria-label={t(
                "studentDashboard.currentProgressSection.moduleCard.progressChart.label",
              )}
              data={progressChartData}
              dataKey="value"
              innerRadius={60}
              startAngle={-270}
              isAnimationActive={true}
            >
              {progressChartData.map(({ id, color, dataCy }) => (
                <Cell key={id} data-cy={dataCy} fill={color} stroke={color} />
              ))}
              <Label
                data-cy="module-avg-progress"
                fill={
                  isInProgress ? theme.palette.primary.brand : theme.palette.feedback.success.brand
                }
                fontWeight="bold"
                position="center"
              >
                {`${Math.round(avgProgress)}%`}
              </Label>
            </Pie>
          </PieChart>
        )}
        {/* -- Module information -- */}
        <Heading3 data-cy="module-card-title">{moduleTitle}</Heading3>
        <ParagraphLarge>
          <SanitizedHtml dirtyHtml={moduleDescription} tagName="span" />
        </ParagraphLarge>
        {extraCardContent}
      </CssFlex>
      <hr />
      <CssButton isBlock variant={actionButtonVariant} onClick={onActionButtonClick}>
        {actionButtonLabel}
      </CssButton>
    </StyledModuleCard>
  );
};
